import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button, useTheme, Box, Typography } from "@mui/material";
import { PlayArrow, Pause, VolumeUp } from "@mui/icons-material";
import { VoiceVisualizer } from "../../../voice-visualizer/voice-visualizer";
import { audioButtonContainerStyles, audioButtonStyles } from "./styles";
import { AudioTranscriptionDialog } from "../audio-transcription-markdown/audio-transcription-dialog";
import type { TranscriptionTimedPayload } from "../../../../types";
import { useAuthenticatedFileUrl } from "../../../../hooks/useAuthenticatedFileUrl";

let sharedAudioContext: AudioContext | null = null;
const TRANSCRIPT_SEEK_PREROLL_SEC = 0.18;
const getAudioContext = () => {
  if (!sharedAudioContext) {
    // @ts-ignore
    const Ctx = window.AudioContext || window.webkitAudioContext;
    sharedAudioContext = new Ctx();
  }
  return sharedAudioContext!;
};

const warmUpAudio = async (ctx: AudioContext) => {
  if (ctx.state !== "running") {
    try {
      await ctx.resume();
    } catch {}
  }
  const buffer = ctx.createBuffer(1, 220, ctx.sampleRate);
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.connect(ctx.destination);
  try {
    src.start(0);
  } catch {}
};

interface AudioButtonProps {
  buttonText: string;
  audioUrl: string;
  onAudioStateChange: (isPlaying: boolean) => void;
  disabled?: boolean;
  transcription?: string;
  transcriptionTimed?: TranscriptionTimedPayload;
}

export const AudioButton: React.FC<AudioButtonProps> = ({
  buttonText,
  audioUrl,
  onAudioStateChange,
  disabled = false,
  transcription,
  transcriptionTimed,
}) => {
  const { url: resolvedAudioUrl } = useAuthenticatedFileUrl(audioUrl);
  const effectiveAudioUrl = resolvedAudioUrl ?? audioUrl;
  const theme = useTheme();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const hasTranscript = !!(transcription?.trim() || transcriptionTimed?.segments?.length);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  const nodeRef = useRef<{
    ctx: AudioContext | null;
    source: MediaElementAudioSourceNode | null;
    gain: GainNode | null;
  }>({ ctx: null, source: null, gain: null });

  const safeSetPlaying = useCallback(
    (flag: boolean) => {
      setIsPlaying(flag);
      onAudioStateChange(flag);
    },
    [onAudioStateChange],
  );

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  useEffect(() => {
    return () => {
      try {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.src = "";
          audioRef.current.load();
          audioRef.current.remove();
          audioRef.current = null;
        }
      } catch {}
      if (nodeRef.current.source) {
        try {
          nodeRef.current.source.disconnect();
        } catch {}
      }
      if (nodeRef.current.gain) {
        try {
          nodeRef.current.gain.disconnect();
        } catch {}
      }
      nodeRef.current = { ctx: nodeRef.current.ctx, source: null, gain: null };
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isPlaying && audioRef.current) {
        audioRef.current.pause();
        safeSetPlaying(false);
      }
    };

    const handleBeforeUnload = () => {
      if (isPlaying && audioRef.current) {
        audioRef.current.pause();
        safeSetPlaying(false);
      }
    };

    const handlePageHide = () => {
      if (isPlaying && audioRef.current) {
        audioRef.current.pause();
        safeSetPlaying(false);
      }
    };

    const handleFocus = () => {};

    const handleBlur = () => {
      if (isPlaying && audioRef.current) {
        audioRef.current.pause();
        safeSetPlaying(false);
      }
    };

    const handleWindowFocus = () => {};

    const handleWindowBlur = () => {
      if (isPlaying && audioRef.current) {
        audioRef.current.pause();
        safeSetPlaying(false);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [isPlaying, safeSetPlaying]);

  const createAudioElement = () => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current.load();
        audioRef.current.remove();
      } catch {}
    }

    const el = new Audio();
    el.preload = "auto";
    el.crossOrigin = "anonymous";
    el.volume = 1.0;
    el.muted = false;
    el.setAttribute("playsinline", "true");
    // @ts-ignore
    el.setAttribute("webkit-playsinline", "true");
    el.style.display = "none";
    document.body.appendChild(el);
    audioRef.current = el;
    return el;
  };

  const connectToWebAudio = (el: HTMLAudioElement) => {
    const ctx = getAudioContext();
    nodeRef.current.ctx = ctx;

    if (nodeRef.current.source) {
      try {
        nodeRef.current.source.disconnect();
      } catch {}
      nodeRef.current.source = null;
    }
    if (!nodeRef.current.gain) {
      nodeRef.current.gain = ctx.createGain();
      nodeRef.current.gain.gain.value = 1.0;
      nodeRef.current.gain.connect(ctx.destination);
    }

    const source = ctx.createMediaElementSource(el);
    source.connect(nodeRef.current.gain!);
    nodeRef.current.source = source;
  };

  const handlePlayPause = async (event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (isPlaying) {
      if (audioRef.current) audioRef.current.pause();
      safeSetPlaying(false);
      return;
    }
    const audioElements = document.querySelectorAll("audio, video");
    audioElements.forEach((element: any) => {
      if (!element.paused && element !== audioRef.current) {
        element.pause();
      }
    });

    setIsLoading(true);
    try {
      const ctx = getAudioContext();

      await ctx.resume();
      await warmUpAudio(ctx);

      const el = createAudioElement();

      connectToWebAudio(el);

      const bust = (u: string) => {
        const url = new URL(u, window.location.href);
        url.searchParams.set("_ts", Date.now().toString());
        return url.toString();
      };

      el.src = bust(effectiveAudioUrl);
      el.playbackRate = playbackRate;

      const onEnded = () => {
        safeSetPlaying(false);
        setCurrentTime(el.duration || 0);
        setIsLoading(false);
        el.removeEventListener("ended", onEnded);
        el.removeEventListener("error", onError);
        el.removeEventListener("play", onPlay);
        el.removeEventListener("timeupdate", onTimeUpdate);
        el.removeEventListener("loadedmetadata", onLoadedMetadata);
        el.removeEventListener("durationchange", onLoadedMetadata);
      };
      const onError = (e: any) => {
        console.error("Audio error:", e);
        safeSetPlaying(false);
        setIsLoading(false);
        el.removeEventListener("ended", onEnded);
        el.removeEventListener("error", onError);
        el.removeEventListener("play", onPlay);
        el.removeEventListener("timeupdate", onTimeUpdate);
        el.removeEventListener("loadedmetadata", onLoadedMetadata);
        el.removeEventListener("durationchange", onLoadedMetadata);
      };
      const onTimeUpdate = () => {
        setCurrentTime(el.currentTime || 0);
      };
      const onLoadedMetadata = () => {
        setDuration(Number.isFinite(el.duration) ? el.duration : 0);
      };
      const onPlay = () => {
        setTimeout(() => {
          if (el.paused) return;
          if (el.volume < 1) el.volume = 1;
          if (nodeRef.current.gain && nodeRef.current.gain.gain.value < 1) {
            nodeRef.current.gain.gain.value = 1;
          }
        }, 200);
      };

      el.addEventListener("ended", onEnded);
      el.addEventListener("error", onError);
      el.addEventListener("play", onPlay);
      el.addEventListener("timeupdate", onTimeUpdate);
      el.addEventListener("loadedmetadata", onLoadedMetadata);
      el.addEventListener("durationchange", onLoadedMetadata);

      await el.play();
      safeSetPlaying(true);
      setIsLoading(false);
    } catch (err) {
      console.error("Error playing audio:", err);
      setIsLoading(false);
      safeSetPlaying(false);
    }
  };

  const handleSeekTranscript = useCallback((sec: number) => {
    if (!audioRef.current) return;
    const seekWithPreroll = Math.max(0, sec - TRANSCRIPT_SEEK_PREROLL_SEC);
    audioRef.current.currentTime = seekWithPreroll;
    setCurrentTime(audioRef.current.currentTime || seekWithPreroll);
    if (audioRef.current.paused) {
      void audioRef.current.play().catch(() => undefined);
    }
  }, []);

  const getButtonIcon = () => {
    if (isLoading) return <VolumeUp />;
    return isPlaying ? <Pause /> : <PlayArrow />;
  };

  if (isPlaying) {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Typography
            sx={{
              color: "white",
              fontSize: "14px",
              fontWeight: 600,
              textShadow: "0 0 4px rgba(0,0,0,0.8)",
              textAlign: "center",
            }}
          >
            🔊 Click to listen again
          </Typography>
          <Box
            onClick={handlePlayPause}
            sx={audioButtonContainerStyles(theme)}
            title="Click to pause and listen again"
          >
            <VoiceVisualizer isPlaying={isPlaying} />
          </Box>
          {hasTranscript && (
            <Button
              size="small"
              variant="text"
              onClick={(e) => {
                e.stopPropagation();
                setTranscriptOpen(true);
              }}
              sx={{
                color: "white",
                textTransform: "none",
                textDecoration: "underline",
                fontSize: "13px",
                minWidth: 0,
              }}
            >
              View transcript
            </Button>
          )}
        </Box>
        <AudioTranscriptionDialog
          open={transcriptOpen}
          onClose={() => setTranscriptOpen(false)}
          markdown={transcription}
          timed={transcriptionTimed}
          currentTimeSec={currentTime}
          onSeekToSecond={handleSeekTranscript}
          audioOverview={{
            audioUrl,
            isPlaying,
            currentTimeSec: currentTime,
            durationSec: duration,
            title: buttonText,
            onPlayPause: handlePlayPause,
            playbackRate,
            onPlaybackRateChange: setPlaybackRate,
          }}
        />
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 0.75,
        }}
      >
        <Button
          variant="outlined"
          startIcon={getButtonIcon()}
          onClick={handlePlayPause}
          disabled={disabled || isLoading}
          sx={audioButtonStyles(theme)}
          title={isLoading ? "Loading audio..." : "Click to play audio"}
        >
          {buttonText}
        </Button>
        {hasTranscript && (
          <Button
            size="small"
            variant="text"
            onClick={() => setTranscriptOpen(true)}
            disabled={disabled}
            sx={{ textTransform: "none", fontSize: "13px" }}
          >
            View transcript
          </Button>
        )}
      </Box>
      <AudioTranscriptionDialog
        open={transcriptOpen}
        onClose={() => setTranscriptOpen(false)}
        markdown={transcription}
        timed={transcriptionTimed}
        currentTimeSec={currentTime}
        onSeekToSecond={handleSeekTranscript}
        audioOverview={{
          audioUrl,
          isPlaying,
          currentTimeSec: currentTime,
          durationSec: duration,
          title: buttonText,
          onPlayPause: handlePlayPause,
          playbackRate,
          onPlaybackRateChange: setPlaybackRate,
        }}
      />
    </>
  );
};
